/**
 * Every Arabic label the user panel renders.
 *
 * TWO sources, in this order, and nothing else:
 *
 *   1. `rawam-ui/apps/user/src/i18n/translations.js` — the reference panel's own
 *      wording. Where it has a key for something, that value is used verbatim
 *      and the key path is noted beside it.
 *   2. `CoEIA.Rawam.UICore/Locale/*.ar.resx` — the legacy resource files, for
 *      the screens and states the reference has no key for (the exam briefing,
 *      the policy download states, the watching states, the certificate).
 *
 * Nothing on this page was written for it. If a screen needs a word neither
 * source has, the screen does without.
 */

/** rawam-ui `common.*`, plus the legacy words for the states it has no key for. */
export const common = {
  viewAll: 'عرض الكل', // common.viewAll
  start: 'ابدأ', // common.start
  continue: 'تابع', // common.continue
  completed: 'مكتمل', // common.completed
  inProgress: 'قيد التنفيذ', // common.inProgress
  notStarted: 'لم يبدأ', // common.notStarted
  mandatory: 'إلزامي', // common.mandatory
  optional: 'اختياري', // common.optional
  minutes: 'دقيقة', // common.minutes
  points: 'نقطة', // common.points
  cancel: 'إلغاء', // common.cancel
  save: 'حفظ', // common.save
  submit: 'إرسال', // common.submit
  close: 'إغلاق', // common.close
  next: 'التالي', // common.next
  previous: 'السابق', // common.previous
  empty: 'لا توجد بيانات', // common.empty
  loading: 'جار التحميل…', // common.loading
  viewDetails: 'عرض التفاصيل', // common.viewDetails
  download: 'تحميل', // common.download
  /* For a control that is declared cosmetic — the API has no route behind it YET. Not legacy:
     the legacy simply had the endpoint. COSMETIC_UI.md. */
  soon: 'غير متاح بعد',
  accept: 'موافق', // common.accept
  attachments: 'المرفقات', // common.attachments
  comments: 'التعليقات', // common.comments
  search: 'ابحث…', // common.search

  /* Surveys.ar.resx TableHead_1/2/4/5 — the column names both list tables use.
     The reference panel has no table of its own, so these stay legacy. */
  serial: 'م',
  title: 'العنوان',
  mandatoryLabel: 'الإلزامية',
  status: 'الحالة',
}

/** SystemMessages.ar.resx — the three words every result badge uses. */
export const outcome = {
  passed: 'نجح',
  notPassed: 'لم ينجح',
  notComplete: 'غير مكتمل',
}

/** rawam-ui `nav.*` and `footer.*`. */
export const shell = {
  courses: 'البرامج التدريبية', // nav.courses
  assessments: 'قياس', // nav.assessments
  surveys: 'الاستبيانات', // nav.surveys
  policies: 'السياسات', // nav.policies
  reports: 'الحوادث السيبرانية', // nav.reports
  awareness: 'النشرات التوعوية', // nav.awareness
  leaderboard: 'لوحة المتصدرين', // nav.ambassador
  contact: 'تواصل معنا', // nav.contact
  statistics: 'إحصائيات', // nav.statistics
  manual: 'دليل المستخدم', // nav.manual
  more: 'المزيد', // nav.more
  profile: 'الملف الشخصي', // nav.profile
  adminPanel: 'لوحة الإدارة', // nav.adminPanel
  changePassword: 'تغيير كلمة المرور', // nav.changePassword
  logout: 'تسجيل الخروج', // nav.logout
  notifications: 'الإشعارات', // nav.notifications
  markAllRead: 'تعليم الكل كمقروء', // common.markAllRead
  copyright: 'جميع الحقوق محفوظة', // footer.copyright
}

/**
 * The three soft prompts `/api/session` reports in `Warnings`, which nothing read.
 *
 * Wording and destinations come from the project's own
 * `Views/Shared/Mandatory{Policies,Survey,Assessment}Modal.cshtml`. Those are full-screen
 * blocking overlays; these are not, because `SessionWarningsDto` says in as many words that
 * they are "not gates: the user keeps browsing, the shell just surfaces them".
 */
export const warnings = {
  policies: 'هناك سياسات جديدة، يرجى الموافقة على كل السياسات التي لم تتم الموافقة عليها',
  surveys: 'هناك استبيانات إلزامية، يرجى الدخول عليها وإنهاؤها',
  assessments: 'هناك قياسات إلزامية، يرجى الدخول عليها وإنهاؤها',
  go: 'الانتقال',
  dismiss: 'إخفاء',
}

/** rawam-ui `userDash.*` and `dashboard.*`. */
/**
 * THE TWO DUTIES THAT HOLD THE PORTAL SHUT.
 *
 * A policy saved with "Force Users to agree" (`ClientPolicy.ForceUsers`) and an unfinished
 * mandatory assessment are not notices — in the legacy product each raised a full-screen
 * `notification-box` over the whole panel with a single link out and no way to dismiss it
 * (`Views/Shared/Mandatory{Policies,Assessment}Modal.cshtml`). This is that, in the
 * portal's own voice. See `PortalGate`.
 */
export const gate = {
  policies: {
    title: 'هناك سياسات بانتظار موافقتك',
    body: 'لا يمكن استخدام المنصة قبل الاطلاع على السياسات الجديدة والموافقة عليها.',
    action: 'الاطلاع والموافقة',
    /* Used when exactly one policy is outstanding and the button opens it by name. */
    one: 'سياسة واحدة بانتظار موافقتك',
    oneBody: 'لا يمكن استخدام المنصة قبل الاطلاع عليها والموافقة عليها.',
    /* And when several are: each gets its own row. */
    manyBody: 'لا يمكن استخدام المنصة قبل الاطلاع على كل واحدة منها والموافقة عليها.',
    openOne: 'الاطلاع والموافقة',
  },
  assessments: {
    title: 'هناك قياسات إلزامية لم تكتمل',
    body: 'لا يمكن استخدام المنصة قبل إنهاء القياسات الإلزامية.',
    action: 'الانتقال إلى القياسات',
    one: 'هناك قياس إلزامي لم يكتمل',
    oneBody: 'لا يمكن استخدام المنصة قبل إنهائه.',
    manyBody: 'لا يمكن استخدام المنصة قبل إنهاء كل واحد منها.',
    openOne: 'فتح القياس',
  },
  signOut: 'تسجيل الخروج',
}

export const dashboard = {
  title: 'إحصائيات', // userDash.title
  subtitle: 'نظرة شاملة على تقدمك في البرامج والدورات وأهم نقاطك وشاراتك.', // userDash.subtitle
  programs: 'البرامج التدريبية', // userDash.programs
  courses: 'الدورات', // userDash.courses
  watching: 'نسبة المشاهدة', // userDash.watching
  successRate: 'نسبة النجاح', // userDash.successRate
  positivePoints: 'النقاط الإيجابية', // userDash.points.positive
  negativePoints: 'النقاط السلبية', // userDash.points.negative
  riskLevel: 'مستوى المخاطر', // userDash.points.risk
  badges: 'شاراتك', // userDash.badges
  badgesEarned: 'مفتوحة', // userDash.badges.earned
  badgesLocked: 'مقفلة', // userDash.badges.locked
  toUnlock: 'لفتح الشارة', // userDash.badges.toUnlock
  continueLearning: 'تابع التعلم', // dashboard.continueLearning
  rank: 'الترتيب', // dashboard.rank
  points: 'النقاط', // dashboard.points

  /**
   * The front page's own words.
   *
   * The legacy dashboard answers "how much have you scored" and nothing about
   * what to do next, so it has no wording for any of this — these are the
   * panel's, and each one names a page the learner already has.
   */
  greeting: 'أهلا بك',
  standing: 'ترتيبك بين المتصدرين',
  todo: 'ما ينتظرك',
  todoEmpty: 'لا شيء ينتظرك — أنت على اطلاع بكل شيء',
  todoAssessments: 'قياسات متاحة الآن',
  todoSurveys: 'استبيانات بانتظار إجابتك',
  todoPolicies: 'سياسات بانتظار إقرارك',
  todoExams: 'اختبارات جاهزة للأداء',
  todoCourses: 'دورات لم تكتمل مشاهدتها',
  latestAwareness: 'أحدث النشرات',
  nextBadge: 'الشارة التالية',
}

/** rawam-ui `courses.*`, plus Courses.ar.resx for the states it has no key for. */
export const courses = {
  availableFrom: 'متاحة من',
  title: 'البرامج التدريبية', // courses.title
  subtitle: 'البرامج التدريبية المخصصة لك — تابع تقدمك واحصل على شهاداتك.', // courses.subtitle
  filterAll: 'الكل', // courses.filter.all
  filterInProgress: 'قيد التنفيذ', // courses.filter.inProgress
  filterCompleted: 'مكتملة', // courses.filter.completed
  filterNotStarted: 'لم تبدأ', // courses.filter.notStarted
  duration: 'المدة', // courses.duration
  chapters: 'الفصول', // courses.chapters
  startCourse: 'ابدأ الآن', // courses.startCourse
  continueCourse: 'متابعة التدريب', // courses.continueCourse
  viewDetails: 'عرض التفاصيل', // courses.viewDetails
  viewCertificate: 'تحميل الشهادة', // courses.viewCertificate
  exam: 'الاختبار النهائي', // courses.exam
  summaryAvailable: 'البرامج المتاحة', // courses.summary.available
  summaryInProgress: 'البرامج قيد التقدم', // courses.summary.inProgress
  summaryCompleted: 'البرامج المكتملة', // courses.summary.completed
  summaryCertificates: 'الشهادات المتاحة', // courses.summary.certificates
  noResults: 'لا توجد نتائج مطابقة', // courses.noResults

  /* Courses.ar.resx — the watching and examination states, and the certificate
     delivery the reference panel has no screen for. */
  survey: 'الاستبيان',
  certificate: 'الشهادة',
  sendCertificate: 'إرسال بالبريد',
  finished: 'انهى المشاهدة',
  started: 'متابعة المشاهدة',
  notStarted: 'لم يبدأ الدورة',
  notAttended: 'لم يدخل الاختبار',
  noContent: 'لا يوجد دورات تدريبية متاحة',
  surveyRequired: 'برجاء ملئ استبيان البرنامج للحصول على الشهادة',
  programCompleteRequired: 'الرجاء إكمال البرنامج',
  programProgress: 'نسبة الإنجاز',
  pdf: 'PDF',
  png: 'PNG',
}

/** CourseDetails.ar.resx — no reference screen covers a course's inside. */
export const courseDetails = {
  interactiveCourse: 'دورة تفاعلية',
  title: 'تفاصيل الدورة التدريبية',
  content: 'المحتوى',
  attachments: 'المرفقات',
  downloadHint: 'اضغط على اسم الملف للتحميل',
  videoCount: 'مقاطع',
  totalDuration: 'الوقت الإجمالي',
  categoriesCount: 'العدد الإجمالي للمقاطع',
  contentType: 'نوع المحتوى',
  watchPercentage: 'نسبة مشاهدة المواضيع',
  courseStart: 'تاريخ بداية الدورة',
  courseEnd: 'تاريخ نهاية الدورة',
  examTries: 'محاولات الاختبار',
  examState: 'حالة الاختبار',
  examStateSuccess: 'نجح في الاختبار',
  examStateFail: 'لم ينجح في الاختبار',
  examStateNotTaken: 'لم يتم دخول الاختبار',
  startExam: 'ابدأ الاختبار',
  examHistory: 'شاهد سجل الاختبارات',
  examModalTitle: 'تنبيه',
  /* The briefing rules, cut to one line each — the mark beside a rule now says
     what kind of rule it is, so the sentence no longer has to. Originals:
     'عند البدء بالاختبار استخدم ازرار التنقل للعودة او الانتقال الى السؤال التالي.'
     'لا تستخدم خيارات المتصفح للتنقل (الرجوع للخلف / التوجه الى الصفح التالية /
      تحديث الصفحة) سيؤدي ذلك الى إنهاء الاختبار.'
     Dropped: 'للبدء بالاختبار اضغط على (ابدأ الاختبار).' — the button in the
     foot of the same dialog already says it. */
  examNavInstruction: 'تنقل بين الأسئلة بأزرار الاختبار.',
  examBackInstruction: 'تجنب إغلاق الصفحة أو تحديثها أثناء الاختبار. استخدم أزرار الاختبار للتنقل بين الأسئلة.',
  examStartDescription: 'أنت على وشك بدء اختبار هذه الدورة. سيحسب البدء محاولة واحدة. اقرأ التعليمات التالية، ثم اضغط بدء الاختبار عندما تكون مستعدا.',
  completeWatch: 'لابد من مشاهدة كامل المحتوى قبل بدء الاختبار',
  noExam: 'الدورة لا تحتوي على إختبار',
  triesCount: '{0} من {1} محاولة',
  playlist: 'المحتوى',
  topicComplete: 'اكتمل الموضوع',
  watchCompleted: 'لقد اتممت مشاهدة الموضوع',
  topicsCompleted: 'لقد شاهدت كامل المحتوى لهذه الدورة',
  nextTopic: 'الموضوع التالي',
  openExam: 'الذهاب إلى الاختبار',
  watchAgain: 'شاهد الموضوع مرة أخرى',
  startContent: 'ابدأ الدورة',
  continueContent: 'متابعة الدورة',
  reviewContent: 'مراجعة الدورة',
  quizTitle: 'اختبار قصير',
  quizInstruction: 'أجب عن جميع الأسئلة للمتابعة إلى الموضوع التالي.',
  subtitleToggle: 'الترجمة',
  mediaUnavailable: 'تعذر تحميل ملف الفيديو. يرجى التواصل مع مسؤول النظام.',
}

/** Exam.ar.resx. */
export const exam = {
  elapsedTime: 'الوقت المستغرق',
  title: 'الاختبار',
  question: 'السؤال',
  counter: '{0} من {1}',
  finish: 'إنهاء',
  optionA: '(أ)',
  optionB: '(ب)',
  optionC: '(ج)',
  optionD: '(د)',
  leaveWarning: 'عفوا ، لا يمكن مغادرة الاختبار دون إكماله او الضغط على زر إنهاء',

  /* Not legacy. The start panel, and what has to be said when a session is lost — the legacy
     simply returned the learner to the start panel without a word. */
  rules: 'بعد البدء لا يمكن إيقاف الاختبار أو العودة إليه لاحقا. أجب عن كل سؤال ثم اضغط إنهاء.',
  start: 'بدء الاختبار',
  next: 'التالي',
  back: 'السابق',
  sessionLostTitle: 'انتهت جلسة الاختبار',
  sessionLost:
    'لم تعد جلسة هذا الاختبار متاحة، ولا يمكن استكماله من حيث توقفت. يمكنك البدء من جديد من صفحة الاختبار.',
  backToStart: 'العودة إلى صفحة الاختبار',
  answerRequired: 'اختر إجابة قبل المتابعة',
}

/** ExamHistory.ar.resx, plus rawam-ui `assessments.history` for the page name. */
export const examHistory = {
  rawamExam: 'نتائج اختبار روام',
  title: 'سجل الاختبارات',
  /* Not legacy. The API answers only "this course's attempts", so the screen has to ask which —
     the reference never had this page on its own, only linked from a course. */
  pickCourse: 'اختر دورة لعرض سجل محاولاتها',
  answersWithheld: 'تفاصيل الإجابات غير متاحة لهذه المحاولة',
  examName: 'اسم الاختبار',
  examDate: 'تاريخ الاختبار',
  percentage: 'النسبة %',
  time: 'الزمن',
  result: 'النتيجة',
  detailsTitle: 'تفاصيل الاختبار',
  correctAnswer: 'الإجابة الصحيحة',
  noExams: 'لا يوجد اختبارات سابقة في هذه الدورة',
  emptyData: 'لا توجد اختبارات سابقة لك',
  passedMessage: 'لقد اجتزت الاختبار',
  notPassedMessage: 'لم تجتاز الاختبار',
}

/** rawam-ui `awareness.*`, plus Awareness.ar.resx. */
export const awareness = {
  title: 'النشرات التوعوية', // nav.awareness
  subtitle: 'مقالات وموارد لتعزيز الوعي الأمني', // awareness.subtitle
  readMore: 'اقرأ المزيد', // awareness.readMore
  attachments: 'المرفقات',
  noContent: 'لا يوجد محتوى متاح',
}

/** rawam-ui `policies.*`, plus WorkspacePolicies.ar.resx for the file states. */
export const policies = {
  title: 'السياسات', // nav.policies
  subtitle: 'اطلع على السياسات وأقر بها', // policies.subtitle
  acceptedOn: 'تم القبول في', // policies.acceptedOn
  preview: 'معاينة المستند', // policies.preview
  acceptPolicy: 'الإقرار بالسياسة', // policies.acceptPolicy

  version: 'الإصدار',
  accept: 'موافقة',
  accepted: 'تمت الموافقة',
  alreadyAccepted: 'تمت الموافقة مسبقا',
  notAccepted: 'لم تتم الموافقة',
  rejected: 'تم الرفض',
  states: { accepted: 'تمت الموافقة', pending: 'بانتظار الموافقة', rejected: 'مرفوضة' },
  viewed: 'تم الإطلاع',
  notViewed: 'لم يتم الإطلاع',
  downloaded: 'تم تحميل الملف',
  notDownloaded: 'لم يتم تحميل الملف',
  uncategorized: 'غير مصنف',
  noContent: 'لا يوجد محتوى متاح',
  /* The policy has a stored file, but it is not on the server - see PolicyDocument. */
  documentMissing: 'تعذر فتح مستند السياسة',
  documentMissingHint: 'الملف غير متوفر على الخادم. تواصل مع مسؤول النظام قبل الموافقة.',
}

/** rawam-ui `assessments.*`, plus Assessments.ar.resx for the window states. */
export const assessments = {
  title: 'قياس', // nav.assessments
  subtitle: 'قس مستواك وتعرف على نقاط التحسين', // assessments.subtitle
  start: 'ابدأ التقييم', // assessments.startAssessment
  history: 'سجل المحاولات', // assessments.history
  open: 'متاحة الآن', // surveys.open

  done: 'لقد أتممت هذا القياس',
  earlyStart: 'لم يبدء هذا القياس إلى الأن',
  finished: 'لقد انتهى الوقت المحدد لهذا القياس',
  notActive: 'القياس الذي تم اختياره غير نشط',

  /* The assessment's own page — `/portal/assessments/:id`. */
  period: 'فترة القياس',
  about: 'عن القياس',
  before: 'قبل البدء',
  once: 'يحتسب القياس مرة واحدة، ولا يمكن إعادته بعد إنهائه.',
  all: 'كل القياسات',
}

/** rawam-ui `surveys.*`, plus Surveys.ar.resx for the answer controls. */
export const surveys = {
  title: 'الاستبيانات', // nav.surveys
  subtitle: 'شاركنا رأيك بسرعة', // surveys.subtitle
  start: 'املأ الاستبانة', // surveys.startSurvey
  thankYou: 'شكرا لمشاركتك!', // surveys.thankYou
  statusAvailable: 'متاحة للاستجابة', // surveys.status.available
  statusCompleted: 'تم إرسال ردك', // surveys.status.completed
  type: 'النوع', // Surveys.ar.resx TableHead_3
  typeGeneral: 'عامة', // surveys.type.general
  typeCourse: 'دورة', // surveys.type.course
  typeProgram: 'برنامج', // surveys.type.program

  submit: 'إنهاء',
  answerYes: 'نعم',
  answerNo: 'لا',
  resetStars: 'مسح',
  textPlaceholder: 'اكتب الإجابة هنا',
  /* Original: 'يوجد بعض الأسئلة التي لم يتم الإجابة عنها أو لم يتم الإجابة عنها
     بشكل صحيح، فضلا قم بمراجعة كافة الأسئلة المشار إليها ثم حاول مرة أخرى'. The
     panel marks the questions in red and scrolls you to the first one, so the
     sentence no longer has to describe what the page is already showing. */
  missingAnswers: 'أجب عن كافة الأسئلة الإلزامية ثم حاول مرة أخرى.',
  missingShort: 'أسئلة إلزامية بلا إجابة',
  saved: 'تم حفظ الإجابات بنجاح',
  noQuestions: 'لم يتم العثور على أسئلة خاصة بهذا الاستبيان',
  /* Cut to one line, as in courseDetails. Original:
     'لا تستخدم خيارات المتصفح للتنقل (الرجوع للخلف / التوجه الى الصفح التالية /
      تحديث الصفحة) سيؤدي ذلك الى فقدان كافة الإجابات المدخلة و عدم احتساب أي
      إجابة.'
     Dropped: 'للبدء الاستبيان، فضلا اضغط على (ابدأ الاستبيان).' — the button in
     the foot of the same dialog already says it. */
  backInstruction: 'لا تستخدم رجوع المتصفح أو تحديث الصفحة — ستفقد إجاباتك.',

  answeredOf: 'تمت الإجابة',
  questionsNav: 'الأسئلة',
  /* Arabic counts its nouns — see countQuestions() in SurveyPage.jsx. */
  questionOne: 'سؤال واحد',
  questionTwo: 'سؤالان',
  questionFew: 'أسئلة',
  questionMany: 'سؤالا',
}

/** rawam-ui `reports.*`, plus SecurityReports.ar.resx for the validators. */
export const reports = {
  reporter: 'مقدم البلاغ',
  details: 'تفاصيل البلاغ',
  thread: 'المحادثة',
  closedNotice: 'هذا البلاغ مغلق، ولا يمكن إضافة تعليقات عليه.',
  noComments: 'لا تعليقات بعد. سيرد فريق الاستجابة هنا.',
  backToList: 'كل البلاغات',
  title: 'الحوادث السيبرانية', // nav.reports
  subtitle: 'بلغ عن أي حادثة أو شك أمني', // reports.subtitle
  create: 'بلاغ جديد', // reports.create
  subject: 'عنوان البلاغ', // reports.subject
  status: 'حالة البلاغ', // reports.status
  created: 'تاريخ البلاغ', // reports.created
  lastUpdate: 'آخر تحديث', // reports.lastUpdate
  number: 'رقم البلاغ', // reports.number
  category: 'فئة البلاغ', // reports.category
  subCategory: 'الفئة الفرعية', // reports.subCategory
  content: 'محتوى البلاغ', // reports.content
  attachments: 'المرفقات', // reports.attachments
  attachmentsHint: 'PNG · JPG · GIF · DOC · DOCX · PDF', // reports.attachmentsHint
  commentAdd: 'إضافة تعليق', // reports.commentAdd
  commentPlaceholder: 'اكتب تعليقك هنا…', // reports.commentPlaceholder
  commentSend: 'إرسال التعليق', // reports.commentSend
  formIntro: 'املأ التفاصيل بدقة لمساعدة فريق الاستجابة على التحرك بسرعة.', // reports.formIntro
  selectCategory: 'اختر الفئة', // reports.selectCategory
  selectSubCategory: 'اختر الفئة الفرعية', // reports.selectSubCategory

  /* The toast after each of the two writes. Both used to show the SERVER's sentence and
     nothing else, which left an English reader with an Arabic one — see serverText(). */
  sent: 'تم إرسال البلاغ',
  commentSent: 'تمت إضافة التعليق',

  titleLength: 'لابد أن يكون عنوان البلاغ بين 3 و 200 حرف',
  contentLength: 'لابد أن يكون النص بين 3 و 2000 حرف',
  commentLength: 'لابد أن يكون النص بين 2 و 1500 حرف',
  /* Resources.ar.resx — the three states a report moves through. */
  stateNew: 'جديد',
  stateInProgress: 'قيد العمل',
  stateClosed: 'إنتهت',
}

/** rawam-ui `leaderboard.*`. */
export const leaderboard = {
  title: 'لوحة المتصدرين', // leaderboard.title
  subtitle: 'ترتيب الموظفين حسب نقاط المشاركة والتفاعل في التوعية السيبرانية.', // leaderboard.subtitle
  username: 'اسم المستخدم', // leaderboard.username
  name: 'الاسم', // leaderboard.name
  department: 'الإدارة', // leaderboard.department
  points: 'النقاط', // leaderboard.points
  badges: 'الشارات', // leaderboard.badges
  rank: 'الترتيب', // leaderboard.rank
  search: 'ابحث في المتصدرين…', // leaderboard.search
  you: 'أنت', // leaderboard.you
  podium: 'منصة التتويج', // leaderboard.podium
  totalPlayers: 'مجموع المشاركين', // leaderboard.totalPlayers
  topScore: 'أعلى نقاط', // leaderboard.topScore
  avgScore: 'متوسط النقاط', // leaderboard.avgScore
  allDepts: 'كل الإدارات', // leaderboard.allDepts
}

/** rawam-ui `profile.*` and `password.*`. */
export const profile = {
  title: 'الملف الشخصي', // profile.title
  subtitle: 'معلوماتك الشخصية والوظيفية في منصة روام.', // profile.subtitle
  email: 'البريد الإلكتروني', // profile.email
  phone: 'الهاتف', // profile.phone
  mobile: 'الجوال', // profile.mobile
  department: 'الإدارة', // profile.department
  username: 'اسم المستخدم', // profile.username
  employeeId: 'الرقم الوظيفي', // profile.employeeId
  arabicName: 'الاسم بالعربية', // profile.arabicName
  englishName: 'الاسم بالإنجليزية', // profile.englishName
  gender: 'الجنس', // profile.gender
  male: 'ذكر', // profile.gender.male
  female: 'أنثى', // profile.gender.female
  account: 'بيانات الحساب', // profile.account
  personal: 'البيانات الشخصية', // profile.personal
  work: 'بيانات العمل', // profile.work
  save: 'حفظ التعديلات', // profile.save
  saved: 'تم حفظ التعديلات', // profile.saved
  /* Every field hidden by the client's profile field setup — nothing left to fill in. */
  noFields: 'لا توجد حقول متاحة للتعديل في ملفك الشخصي',

  changePassword: 'تغيير كلمة المرور', // password.title
  currentPassword: 'كلمة المرور الحالية', // password.current
  newPassword: 'كلمة المرور الجديدة', // password.new
  confirmPassword: 'تأكيد كلمة المرور', // password.confirm
  /* ChangePassword.ar.resx — the server's own two outcomes. */
  passwordChanged: 'تم تغيير كلمه المرور بنجاح',
  passwordFailed:
    'فشلت العملية ربما كلمة المرور الحالية خاطئه، يجب أن تحتوي كلمة المرور على 8 أحرف على الأقل وعلى حرف كبير وعلى رمز ورقم',
  /* UserProfile.ar.resx — the one line the legacy prints under the heading. */
  nameNote:
    'يظهر الاسم في الشهادة كما تكتبه هنا، لذلك تأكد من كتابته بالطريقة التي ترغب بها.',
  language: 'لغة الموقع المفضلة',
  arabic: 'عربي',
  english: 'إنجليزي',
  startDate: 'تاريخ البدء',
}

/** rawam-ui `contact.*`, plus ContactUs.ar.resx for the two fields. */
export const contact = {
  title: 'تواصل معنا', // nav.contact
  subtitle: 'فريق الدعم في خدمتك', // contact.subtitle
  send: 'إرسال الرسالة', // contact.send

  messageTitle: 'عنوان الرسالة',
  messageContent: 'موضوع الرسالة',
  emailLabel: 'بريد الدعم الفني',
  phoneLabel: 'هاتف الدعم الفني',
  titleInvalid: 'لابد أن يكون النص في حدود 2 إلى 200 حرف',
  contentInvalid: 'لابد أن يكون النص في حدود 2 إلى 2000 حرف',
}

/** rawam-ui `certificate.*`, plus ProgramCertificate.ar.resx for the table. */
export const certificates = {
  title: 'شهادة الإنجاز', // certificate.title
  eyebrow: 'شهادة معتمدة', // certificate.eyebrow
  issuedOn: 'تاريخ الإصدار', // certificate.issuedOn

  subtitle:
    'حمل شهادة البرنامج الذي تم اجتيازه. ملاحظة: يجب النجاح في كل الدورات التدريبة بداخل البرنامج التدريبي حتى تتمكن من تحميل الشهادة',
  programDetails: 'تفاصيل البرنامج التدريبي',
  courseName: 'اسم الدورة التدريبية',
  watchState: 'حالة المشاهدة',
  examState: 'حالة الاختبار',
  examTries: 'محاولات الاختبار',
  start: 'البداية',
  end: 'النهاية',
  noContent:
    'لا يوجد برامج أو دورات تدريبية في النظام، لا بد من وجود برنامج تدريبي يحتوي على دورة تدريبية واحدة على الاقل حتى تستطيع استخدام نظام روام',
}

/** rawam-ui `manual.*`. */
export const manual = {
  title: 'دليل المستخدم', // manual.title
  subtitle: 'كل ما تحتاج معرفته عن استخدام المنصة', // manual.subtitle
  pdf: 'مستند PDF',
  video: 'فيديو',
  updated: 'آخر تحديث',
  openInTab: 'فتح في نافذة جديدة',
  emptyTitle: 'لا يوجد دليل مستخدم بعد',
  emptyBody: 'لم تنشر جهتك دليلا للمستخدم حتى الآن، وسيظهر هنا فور نشره.',
  upload: 'رفع الدليل من الإعدادات',
}

/** TermsOfUse.ar.resx. The body itself is a Settings row an administrator
 *  writes, so the panel renders whatever the installation has — never its own. */
export const terms = {
  title: 'شروط الاستخدام',
  subtitle:
    'يجب الموافقة على شروط الاستخدام من أجل المتابعة و القدرة على استخدام النظام، فضلا قم بقراءة كافة شروط الاستخدام ثم أضغط على زر الموافقة.',
  agree: 'أوافق',
  logout: 'تسجيل خروج',
  /* Shown instead of the subtitle when the document changed after the user last
     agreed — being asked again is not the same as being asked the first time. */
  updated: 'تم تحديث شروط الاستخدام، فضلا قم بمراجعتها والموافقة عليها للمتابعة.',
  /* The answer did not reach the server, so the gate stays shut. */
  failed: 'تعذر حفظ ردك، فضلا حاول مرة أخرى.',
}
